import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    TextField,
    Divider,
    CircularProgress,
    Snackbar,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { secureFetch } from "../../../utils/secureFetch";
import { fetchArticleOnline } from "../../../services/articleApi";
import { Release } from "../../../models/Release";

const UserLists = (
    {
        user,
        apiBase,
        isAuthenticated,
        login,
    }) => {

    const [lists, setLists] = useState([]);
    const [loadingLists, setLoadingLists] = useState(false);
    const [selectedList, setSelectedList] = useState(null);
    const [loadingItems, setLoadingItems] = useState(false);

    // editing state
    const [editingKey, setEditingKey] = useState(null);
    const [editingValue, setEditingValue] = useState('');
    // snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });

    useEffect(() => {
        if (!isAuthenticated) {
            login();
            return;
        }
        setLoadingLists(true);
        secureFetch(`${apiBase}/lists`, {}, true)
            .then(res => {
                if (!res.ok) throw new Error();
                return res.json();
            })
            .then(data => setLists(data))
            .catch(err => console.error('Error loading lists:', err))
            .finally(() => setLoadingLists(false));
    }, [isAuthenticated, login, user, apiBase]);

    const handleDeleteList = name => {
        secureFetch(`${apiBase}/lists/${encodeURIComponent(name)}?drop=true`, {
            method: 'DELETE',
        }, true)
            .then(res => {
                if (!res.ok) throw new Error();
            })
            .then(() => setLists(prev => prev.filter(l => l.name !== name)))
            .catch(err => console.error('Error deleting list:', err));
    };

    const handleDragEnd = async (result) => {
        if (!result.destination || !selectedList) return;

        // 1) lokal umsortieren (optimistic UI)
        const items = Array.from(selectedList.articles);
        const [moved] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, moved);

        const prev = selectedList.articles;
        setSelectedList(s => ({ ...s, articles: items }));

        // 2) Reihenfolge an Backend persistieren
        const order = items.map(a => makeKey(a));
        try {
            const res = await secureFetch(
                `${apiBase}/lists/${encodeURIComponent(selectedList.name)}/order`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ order }),
                }, true
            );
            if (!res.ok) throw new Error(`Status ${res.status}`);
            setSnackbar({ open: true, message: 'New Order saved' });
        } catch (e) {
            console.error('Failed to persist order:', e);
            // Rollback
            setSelectedList(s => ({ ...s, articles: prev }));
            setSnackbar({ open: true, message: 'New Order could not be saved!' });
        }
    };

    const handleSelectList = name => {
        setLoadingItems(true);
        secureFetch(`${apiBase}/lists/${encodeURIComponent(name)}`, {}, true)
            .then(res => {
                if (!res.ok) throw new Error();
                return res.json();
            })
            .then(async data => {
                const enriched = await Promise.all(
                    data.articles.map(async art => {

                        const release = new Release({
                            language: art.language,
                            version: art.version,
                            product: art.product
                        });
                        // Keep saved entries usable even if their article is unavailable.
                        let result = { ...art, title: art.title || art.article_id };

                        try {
                            await fetchArticleOnline({
                                id: art.article_id,
                                selectedRelease: release,
                                setArticle: (data) => {
                                    result = { ...art, title: data.title || result.title };
                                },
                                inc: () => { },
                                dec: () => { },
                                setError: () => { },
                            });
                        } catch (err) {
                            console.error('Error loading list article:', art.article_id, err);
                        }

                        return result;
                    })
                );
                setSelectedList({ ...data, articles: enriched });
            })
            .catch(err => console.error('Error loading items:', err))
            .finally(() => setLoadingItems(false));
    };

    const handleDeleteItem = article => {
        secureFetch(`${apiBase}/lists/${encodeURIComponent(selectedList.name)}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(article),
        }, true)
            .then(res => {
                if (!res.ok) throw new Error();
            })
            .then(() =>
                setSelectedList(prev => ({
                    ...prev,
                    articles: prev.articles.filter(a =>
                        !(
                            a.article_id === article.article_id &&
                            a.version === article.version &&
                            a.language === article.language &&
                            a.product === article.product
                        )
                    ),
                }))
            )
            .catch(err => console.error('Error deleting article:', err));
    };

    const handleOpenArticle = (article) => {
        const url = `${window.location.origin}${window.location.pathname}` +
            `?articleId=${encodeURIComponent(article.article_id)}` +
            `&ver=${encodeURIComponent(article.version)}` +
            `&lang=${encodeURIComponent(article.language)}` +
            `&product=${encodeURIComponent(article.product)}`;

        window.open(url, '_blank');
    };

    // begin inline edit
    const makeKey = art =>
        `${art.article_id}__${art.version}__${art.language}__${art.product}`;

    const startEdit = art => {
        setEditingKey(makeKey(art));
        setEditingValue(art.custom_name || '');
    };

    const cancelEdit = () => {
        setEditingKey(null);
        setEditingValue('');
    };

    const saveEdit = art => {
        const payload = {
            article_id: art.article_id,
            version: art.version,
            language: art.language,
            product: art.product,
            custom_name: editingValue.trim(),
        };
        secureFetch(`${apiBase}/lists/${encodeURIComponent(selectedList.name)}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        }, true)
            .then(res => {
                if (!res.ok) throw new Error(`Status ${res.status}`);
                // update local state
                setSelectedList(prev => ({
                    ...prev,
                    articles: prev.articles.map(a =>
                        makeKey(a) === editingKey ? { ...a, custom_name: editingValue.trim() } : a
                    ),
                }));
                setSnackbar({ open: true, message: 'Name saved' });
            })
            .catch(err => {
                console.error('Error saving name:', err);
                setSnackbar({ open: true, message: 'Failed to save name' });
            })
            .finally(cancelEdit);
    };
    // end inline edit

    const handleSnackbarClose = () => {
        setSnackbar(s => ({ ...s, open: false }));
    };

    // DETAIL VIEW
    if (selectedList) {
        return (
            <Box sx={{ width: '100%', position: 'relative' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 1,
                    }}
                >
                    <IconButton onClick={() => setSelectedList(null)} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" noWrap>
                        {selectedList.name}
                    </Typography>
                    <IconButton
                        onClick={() => handleDeleteList(selectedList.name)}
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Box>

                {loadingItems ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="articles">
                            {(provided) => (
                                <List
                                    dense
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {selectedList.articles.map((article, index) => {
                                        const key = makeKey(article);
                                        const isEditing = key === editingKey;

                                        return (
                                            <Draggable
                                                key={key}
                                                draggableId={key}
                                                index={index}
                                                isDragDisabled={isEditing} // nicht ziehen während Inline-Edit
                                            >
                                                {(dragProvided, snapshot) => (
                                                    <React.Fragment>
                                                        <ListItem
                                                            disablePadding
                                                            ref={dragProvided.innerRef}
                                                            {...dragProvided.draggableProps}
                                                        >
                                                            <ListItemButton
                                                                sx={{
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                    boxShadow: snapshot.isDragging ? 2 : 'none',
                                                                    cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                                                }}
                                                                // Ganze Zeile als Griff
                                                                {...dragProvided.dragHandleProps}
                                                            >
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography variant="body1">
                                                                        {article.title}
                                                                    </Typography>

                                                                    {isEditing ? (
                                                                        <TextField
                                                                            value={editingValue}
                                                                            onChange={e => setEditingValue(e.target.value)}
                                                                            onBlur={() => saveEdit(article)}
                                                                            onKeyDown={e => {
                                                                                if (e.key === 'Enter') saveEdit(article);
                                                                                else if (e.key === 'Escape') cancelEdit();
                                                                            }}
                                                                            size="small"
                                                                            autoFocus
                                                                            placeholder="Custom title…"
                                                                        />
                                                                    ) : (
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                color: article.custom_name ? 'text.primary' : 'text.disabled',
                                                                                cursor: 'text'
                                                                            }}
                                                                            onClick={() => startEdit(article)}
                                                                        >
                                                                            {article.custom_name || 'Add custom title for this document…'}
                                                                        </Typography>
                                                                    )}
                                                                </Box>

                                                                <Box>
                                                                    <IconButton onClick={() => handleOpenArticle(article)} size="small">
                                                                        <OpenInNewIcon fontSize="inherit" />
                                                                    </IconButton>
                                                                    <IconButton onClick={() => handleDeleteItem(article)} size="small">
                                                                        <DeleteIcon fontSize="inherit" />
                                                                    </IconButton>
                                                                </Box>
                                                            </ListItemButton>
                                                        </ListItem>
                                                        <Divider component="li" />
                                                    </React.Fragment>
                                                )}
                                            </Draggable>
                                        );
                                    })}

                                    {provided.placeholder}

                                    {selectedList.articles.length === 0 && (
                                        <Typography variant="body2" sx={{ p: 1 }}>
                                            No articles in this list.
                                        </Typography>
                                    )}
                                </List>
                            )}
                        </Droppable>
                    </DragDropContext>
                )}

                <Snackbar
                    open={snackbar.open}
                    message={snackbar.message}
                    autoHideDuration={3000}
                    onClose={handleSnackbarClose}
                />
            </Box>
        );
    }

    // OVERVIEW
    return (
        <Box sx={{ width: '100%' }}>
            {loadingLists ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : lists.length === 0 ? (
                <Typography
                    variant="body2"
                    sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}
                >
                    You haven’t added any collections yet. You can store articles in
                    custom collections that allow you to access them later. To add an
                    article or create a new collection, click the + Icon next to its
                    title in the reader page.
                </Typography>
            ) : (
                <List dense>
                    {lists.map(list => (
                        <ListItem key={list._id} disablePadding>
                            <ListItemButton onClick={() => handleSelectList(list.name)}>
                                <ListItemText primary={list.name} />
                                <IconButton
                                    edge="end"
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleDeleteList(list.name);
                                    }}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="inherit" />
                                </IconButton>
                            </ListItemButton>
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
};

export default UserLists;
